<template>
  <div class="feedback-form">
    <div class="ratings">
      <div v-for="q in ratingQuestions" :key="q.key" class="rating-item" :class="{ 'rating-error': errors.includes(q.key) }" :ref="el => { if (el) itemRefs[q.key] = el }">
        <label>{{ q.label }}</label>
        <div class="num-row">
          <button v-for="n in 11" :key="n-1" class="num-btn" :class="{ active: ratings[q.key] === n-1 }" @click="selectRating(q.key, n-1)">{{ n-1 }}</button>
        </div>
        <div class="num-labels"><span>Not at all</span><span>Extremely</span></div>
      </div>
    </div>

    <div class="flags-section">
      <label class="flags-label">Which of these apply to this answer? Select all that apply.</label>
      <div class="flags-grid">
        <label v-for="f in flagOptions" :key="f" class="flag-option" :class="{ active: flags.includes(f) }">
          <input type="checkbox" :value="f" v-model="flags" class="sr-only">
          <span>{{ f }}</span>
        </label>
      </div>
    </div>

    <div class="comment-section">
      <label>Anything else you want to add?</label>
      <textarea v-model="comment" placeholder="Optional..." rows="3"></textarea>
    </div>

    <button class="primary-btn" @click="submit">Submit & Continue</button>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'
export default {
  name: 'FeedbackForm',
  emits: ['submit'],
  setup(_, { emit }) {
    const ratingQuestions = [
      { key: 'specific', label: 'Did this feel specific to you?' },
      { key: 'accurate', label: 'How much did this feel true or accurate for you?' },
      { key: 'clear', label: 'How clear and understandable was this answer?' },
      { key: 'useful', label: 'How useful was this answer for you?' },
      { key: 'tone', label: 'How right did the tone feel?' },
      { key: 'astrological', label: 'How astrology-based did this feel, rather than generic advice?' },
      { key: 'overall', label: 'Overall, how good was this answer?' }
    ]

    const flagOptions = [
      'Too generic', 'Too vague', 'Too long', 'Too negative', 'Too dramatic',
      'Did not feel personal', 'Did not feel astrological', 'Felt inaccurate',
      'Actually helpful', 'Surprisingly accurate'
    ]

    const ratings = reactive({
      specific: null, accurate: null, clear: null, useful: null, tone: null, astrological: null, overall: null
    })
    const flags = ref([])
    const comment = ref('')
    const errors = ref([])
    const itemRefs = {}

    const selectRating = (key, val) => {
      ratings[key] = val
      errors.value = errors.value.filter(k => k !== key)
    }

    const submit = () => {
      const missing = ratingQuestions.filter(q => ratings[q.key] === null).map(q => q.key)
      if (missing.length > 0) {
        errors.value = missing
        const firstEl = itemRefs[missing[0]]
        if (firstEl) firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      emit('submit', {
        ratings: { ...ratings },
        flags: [...flags.value],
        comment: comment.value
      })
      Object.keys(ratings).forEach(k => ratings[k] = null)
      flags.value = []
      comment.value = ''
      errors.value = []
    }

    return { ratingQuestions, flagOptions, ratings, flags, comment, errors, itemRefs, selectRating, submit }
  }
}
</script>

<style scoped>
.feedback-form { display: flex; flex-direction: column; gap: 40px; padding: 36px 0; border-top: 1px solid rgba(255,255,255,0.06); margin-top: 28px; }
.ratings { display: flex; flex-direction: column; gap: 40px; }
.rating-item label { font-size: 15px; color: #fff; margin-bottom: 16px; display: block; font-weight: 600; line-height: 1.4; text-align: center; }
.rating-error label { color: #f87171; }
.rating-error .num-btn { border-color: rgba(248,113,113,0.3); }
.rating-item { text-align: center; }
.num-row { display: flex; gap: 6px; justify-content: center; }
.num-btn { width: 40px; height: 40px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.5); font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.15s; font-family: inherit; }
.num-btn:hover { background: rgba(108,99,255,0.1); }
.num-btn.active { background: #6c5ce7; border-color: #6c5ce7; color: #fff; transform: scale(1.1); }
.num-labels { display: flex; justify-content: space-between; font-size: 11px; color: rgba(255,255,255,0.3); margin-top: 4px; width: 500px; max-width: 100%; margin-left: auto; margin-right: auto; }
.flags-section { display: flex; flex-direction: column; gap: 16px; text-align: center; }
.flags-label { font-size: 15px; color: #fff; font-weight: 600; }
.flags-grid { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; }
.flag-option { padding: 10px 16px; border-radius: 10px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: #fff; font-size: 14px; cursor: pointer; transition: all 0.15s; }
.flag-option.active { background: rgba(108,99,255,0.1); border-color: rgba(108,99,255,0.3); color: #a29bfe; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); }
.comment-section { display: flex; flex-direction: column; gap: 14px; text-align: center; }
.comment-section label { font-size: 15px; color: #fff; font-weight: 600; }
.comment-section textarea { padding: 14px 16px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15); border-radius: 12px; color: #fff; font-size: 14px; resize: vertical; font-family: inherit; text-align: left; }
.comment-section textarea::placeholder { color: rgba(255,255,255,0.3); }
.comment-section textarea:focus { outline: none; border-color: rgba(255,255,255,0.3); }
.primary-btn { padding: 16px 24px; background: #6c5ce7; border: none; border-radius: 12px; color: #fff; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s; width: 100%; }
.primary-btn:hover { background: #7c6ff0; box-shadow: 0 6px 20px rgba(108,99,255,0.35); }
</style>
